import { playerAppearance } from '../animation/Theme';
import Phaser from 'phaser';
import { GAME_W, GAME_H, MAP_H, HUD_HEIGHT, WORLD_NAMES } from '../constants';
import { type PowerUpType } from '../types';

export interface HUDData {
  score: number;
  lives: number;
  timeLeft: number;
  level: number;
  world: number;
  maxBombs: number;
  activeBombs: number;
  blastRadius: number;
  speedMult: number;
  powerups: PowerUpType[];
}

const HUD_Y = MAP_H;
const PANEL_H = HUD_HEIGHT - 4;
const PANEL_Y = HUD_Y + 2;

const LABEL_STYLE = {
  fontFamily: 'Arial Black, Arial',
  fontSize: '8px',
  color: '#8899bb',
};
const VALUE_STYLE = {
  fontFamily: 'Arial Black, Arial',
  fontSize: '14px',
  color: '#ffdd44',
  stroke: '#000000',
  strokeThickness: 2,
};
const ICON_VALUE_STYLE = {
  fontFamily: 'Arial Black, Arial',
  fontSize: '13px',
  color: '#ffffff',
  stroke: '#000000',
  strokeThickness: 2,
};

export class HUD {
  private scene: Phaser.Scene;

  private bgGfx:  Phaser.GameObjects.Graphics;
  private iconGfx: Phaser.GameObjects.Graphics;

  // Text nodes
  private livesValue:  Phaser.GameObjects.Text;
  private scoreValue:  Phaser.GameObjects.Text;
  private timeValue:   Phaser.GameObjects.Text;
  private levelValue:  Phaser.GameObjects.Text;

  // Right icon counts
  private bombCount:   Phaser.GameObjects.Text;
  private fireCount:   Phaser.GameObjects.Text;
  private speedCount:  Phaser.GameObjects.Text;
  private shieldCount: Phaser.GameObjects.Text;

  // Flash overlay
  private flashRect:  Phaser.GameObjects.Rectangle;
  private flashTween: Phaser.Tweens.Tween | null = null;

  // Track state for re-draws
  private practiceMode = false;
  private elapsedMode = false;
  setElapsedMode(enabled: boolean): void { this.elapsedMode = enabled; this.lastTime = -1; }
  setPracticeMode(enabled: boolean): void { this.practiceMode = enabled; this.lastTime = -1; }

  private lastLives = -1;
  private lastScore = -1;
  private lastTime  = -1;
  private lastLevel = -1;
  private lastWorld = -1;
  private lastBombs = '';

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    const depth = 100;

    // ── Background ─────────────────────────────────────────────────────────────
    this.bgGfx = scene.add.graphics().setDepth(depth).setScrollFactor(0);
    this.drawBackground();

    // ── Panel icons drawn once ─────────────────────────────────────────────────
    this.iconGfx = scene.add.graphics().setDepth(depth + 2).setScrollFactor(0);
    this.drawStaticIcons();

    // Layout: panels evenly spread across GAME_W
    //   [penguin+lives] | [SCORE] | [TIME] | [LEVEL] | [bombs][fire][speed][shield]
    const cy = PANEL_Y + PANEL_H / 2;

    // ── Penguin + lives ────────────────────────────────────────────────────────
    this.livesValue = scene.add.text(52, cy + 3, 'x 3', {
      ...ICON_VALUE_STYLE,
      fontSize: '15px',
    }).setOrigin(0, 0.5).setScrollFactor(0).setDepth(depth + 3);

    // ── SCORE ──────────────────────────────────────────────────────────────────
    scene.add.text(186, cy - 12, 'SCORE', LABEL_STYLE)
      .setOrigin(0.5, 0).setScrollFactor(0).setDepth(depth + 3);
    this.scoreValue = scene.add.text(186, cy + 3, '000000', {
      ...VALUE_STYLE, fontSize: '16px',
    }).setOrigin(0.5, 0.5).setScrollFactor(0).setDepth(depth + 3);

    // ── TIME ───────────────────────────────────────────────────────────────────
    scene.add.text(340, cy - 12, 'TIME', LABEL_STYLE)
      .setOrigin(0.5, 0).setScrollFactor(0).setDepth(depth + 3);
    this.timeValue = scene.add.text(340, cy + 3, '03:00', {
      ...VALUE_STYLE,
    }).setOrigin(0.5, 0.5).setScrollFactor(0).setDepth(depth + 3);

    // ── LEVEL ──────────────────────────────────────────────────────────────────
    scene.add.text(480, cy - 12, 'LEVEL', LABEL_STYLE)
      .setOrigin(0.5, 0).setScrollFactor(0).setDepth(depth + 3);
    this.levelValue = scene.add.text(480, cy + 3, '1-1', {
      ...VALUE_STYLE,
    }).setOrigin(0.5, 0.5).setScrollFactor(0).setDepth(depth + 3);

    // ── Right power-up icons ────────────────────────────────────────────────────
    // bomb icon at 590, fire at 660, speed at 730, shield at 800
    this.bombCount  = this.makeIconCount(600, cy + 3, '1');
    this.fireCount  = this.makeIconCount(680, cy + 3, '2');
    this.speedCount = this.makeIconCount(760, cy + 3, '1');
    this.shieldCount= this.makeIconCount(840, cy + 3, '0');

    // ── Flash overlay ───────────────────────────────────────────────────────────
    this.flashRect = scene.add
      .rectangle(0, 0, GAME_W, GAME_H, 0xffffff, 0)
      .setOrigin(0, 0)
      .setScrollFactor(0)
      .setDepth(200)
      .setBlendMode(Phaser.BlendModes.ADD);
  }

  // ── Drawing helpers ─────────────────────────────────────────────────────────

  private drawBackground(): void {
    const g = this.bgGfx;
    g.clear();

    // Dark navy base
    g.fillStyle(0x0d1b2e, 1);
    g.fillRect(0, HUD_Y, GAME_W, HUD_HEIGHT);

    // Slight lighter top edge
    g.fillStyle(0x1e3a5f, 1);
    g.fillRect(0, HUD_Y, GAME_W, 2);

    // Inner panel cards
    const panels = [
      { x: 4,    w: 130 }, // lives
      { x: 140,  w: 170 }, // score
      { x: 316,  w: 130 }, // time
      { x: 452,  w: 130 }, // level
      { x: 588,  w: 80  }, // bomb
      { x: 674,  w: 80  }, // fire
      { x: 754,  w: 80  }, // speed
      { x: 840,  w: 80  }, // shield
    ];
    for (const p of panels) {
      // Card background
      g.fillStyle(0x122040, 0.9);
      g.fillRoundedRect(p.x, PANEL_Y, p.w, PANEL_H, 6);
      // Card border
      g.lineStyle(1, 0x2a5080, 0.8);
      g.strokeRoundedRect(p.x, PANEL_Y, p.w, PANEL_H, 6);
      // Subtle top-edge shine
      g.fillStyle(0x4488cc, 0.15);
      g.fillRoundedRect(p.x + 2, PANEL_Y + 2, p.w - 4, 4, 3);
    }
  }

  private drawStaticIcons(): void {
    const g = this.iconGfx;
    g.clear();
    const cy = PANEL_Y + PANEL_H / 2;

    const appearance = playerAppearance();
    this.scene.add.image(26, cy, appearance.texture, appearance.frame)
      .setOrigin(0.5, 0.7).setScale(0.24).setDepth(102).setScrollFactor(0);

    // ── Bomb icon ─────────────────────────────────────────────────────────────
    this.drawHudBombIcon(g, 565, cy);

    // ── Fire icon ─────────────────────────────────────────────────────────────
    this.drawHudFireIcon(g, 645, cy);

    // ── Boot/speed icon ───────────────────────────────────────────────────────
    this.drawHudBootIcon(g, 725, cy);

    // ── Shield icon ───────────────────────────────────────────────────────────
    this.drawHudShieldIcon(g, 805, cy);
  }

  private drawHudBombIcon(g: Phaser.GameObjects.Graphics, x: number, y: number): void {
    g.fillStyle(0x111111, 1);
    g.fillCircle(x, y + 3, 10);
    g.fillStyle(0x444444, 1);
    g.fillCircle(x - 3, y, 4);
    g.lineStyle(2, 0x888844, 1);
    g.beginPath(); g.moveTo(x + 4, y - 6); g.lineTo(x + 9, y - 11); g.strokePath();
    g.fillStyle(0xffaa00, 1);
    g.fillCircle(x + 9, y - 12, 3);
  }

  private drawHudFireIcon(g: Phaser.GameObjects.Graphics, x: number, y: number): void {
    g.fillStyle(0xff4400, 1);
    g.fillEllipse(x, y + 4, 14, 18);
    g.fillStyle(0xff8800, 1);
    g.fillEllipse(x, y + 4, 9, 12);
    g.fillStyle(0xffee00, 1);
    g.fillEllipse(x, y + 5, 5, 7);
    g.fillStyle(0xff4400, 1);
    g.fillEllipse(x - 3, y - 4, 5, 9);
    g.fillEllipse(x + 3, y - 2, 4, 7);
  }

  private drawHudBootIcon(g: Phaser.GameObjects.Graphics, x: number, y: number): void {
    // Simple boot shape
    g.fillStyle(0x4499ff, 1);
    g.fillRoundedRect(x - 6, y - 2, 14, 9, 3);
    g.fillStyle(0x88ccff, 1);
    g.fillRoundedRect(x - 7, y + 6, 16, 5, 2);
    // Lace
    g.fillStyle(0xffffff, 0.7);
    g.fillRect(x - 3, y - 2, 2, 5);
    g.fillRect(x + 1, y - 2, 2, 5);
  }

  private drawHudShieldIcon(g: Phaser.GameObjects.Graphics, x: number, y: number): void {
    g.fillStyle(0x2255aa, 1);
    g.beginPath();
    g.moveTo(x, y - 11);
    g.lineTo(x + 9, y - 5);
    g.lineTo(x + 7, y + 9);
    g.lineTo(x, y + 13);
    g.lineTo(x - 7, y + 9);
    g.lineTo(x - 9, y - 5);
    g.closePath();
    g.fillPath();
    g.fillStyle(0x4488dd, 1);
    g.beginPath();
    g.moveTo(x, y - 8);
    g.lineTo(x + 6, y - 3);
    g.lineTo(x + 4, y + 6);
    g.lineTo(x, y + 10);
    g.lineTo(x - 4, y + 6);
    g.lineTo(x - 6, y - 3);
    g.closePath();
    g.fillPath();
    g.fillStyle(0xaaccff, 0.7);
    g.fillRect(x - 1, y - 5, 2, 12);
    g.fillRect(x - 5, y, 10, 2);
  }

  private makeIconCount(x: number, cy: number, val: string): Phaser.GameObjects.Text {
    return this.scene.add.text(x, cy, val, {
      ...ICON_VALUE_STYLE,
      fontSize: '15px',
    }).setOrigin(0, 0.5).setScrollFactor(0).setDepth(103);
  }

  // ── Public API ──────────────────────────────────────────────────────────────

  update(data: HUDData): void {
    this.updateScore(data.score);
    this.updateLives(data.lives);
    this.updateTime(data.timeLeft);
    this.updateLevel(data.level, data.world);
    this.updateBombs(data.activeBombs, data.maxBombs, data.blastRadius, data.speedMult);
  }

  updateScore(score: number): void {
    if (score === this.lastScore) return;
    this.lastScore = score;
    this.scoreValue.setText(String(score).padStart(6, '0'));
  }

  updateLives(lives: number): void {
    if (lives === this.lastLives) return;
    this.lastLives = lives;
    this.livesValue.setText(`x ${Math.max(0, lives)}`);
    this.livesValue.setColor(lives <= 1 ? '#ff4444' : '#ffffff');
  }

  updateTime(seconds: number): void {
    if (this.practiceMode) { this.timeValue.setText('LIBER').setColor('#80e9dc'); return; }
    const s = Math.max(0, Math.ceil(seconds));
    if (s === this.lastTime) return;
    this.lastTime = s;
    const mins = Math.floor(s / 60);
    const secs = s % 60;
    this.timeValue.setText(`${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`);
    if (this.elapsedMode) this.timeValue.setColor('#80e9dc');
    else if (s <= 10)       this.timeValue.setColor('#ff2222');
    else if (s <= 30)  this.timeValue.setColor('#ffaa00');
    else               this.timeValue.setColor('#ffdd44');
  }

  updateLevel(level: number, world: number): void {
    if (level === this.lastLevel && world === this.lastWorld) return;
    this.lastLevel = level;
    this.lastWorld = world;
    this.levelValue.setText(`${world}-${level}`);
    void WORLD_NAMES; // keep import live
  }

  updateBombs(current: number, max: number, radius: number, speed: number): void {
    const key = `${current},${max},${radius},${speed}`;
    if (key === this.lastBombs) return;
    this.lastBombs = key;
    this.bombCount.setText(`${current}/${max}`);
    this.fireCount.setText(String(radius));
    this.speedCount.setText(speed > 1.05 ? `x${speed.toFixed(1)}` : '1');
    this.shieldCount.setText(''); // shield shown via activepower
  }

  // Legacy update shims kept to avoid breaking callers
  updateRadius(_r: number): void { /* merged into updateBombs */ }
  updateSpeed(_m: number): void  { /* merged into updateBombs */ }
  refreshPowerupIcons(_p: PowerUpType[]): void { /* icons always shown */ }

  flash(color = 0xffffff, alpha = 0.35): void {
    if (this.flashTween) {
      this.flashTween.stop();
    }
    this.flashRect.setFillStyle(color, alpha);
    this.flashTween = this.scene.tweens.add({
      targets: this.flashRect,
      alpha: { from: alpha, to: 0 },
      ease: 'Linear',
      duration: 250,
      onComplete: () => {
        this.flashRect.setAlpha(0);
        this.flashTween = null;
      },
    });
  }

  destroy(): void {
    if (this.flashTween) { this.flashTween.stop(); this.flashTween = null; }
    this.bgGfx.destroy();
    this.iconGfx.destroy();
    this.livesValue.destroy();
    this.scoreValue.destroy();
    this.timeValue.destroy();
    this.levelValue.destroy();
    this.bombCount.destroy();
    this.fireCount.destroy();
    this.speedCount.destroy();
    this.shieldCount.destroy();
    this.flashRect.destroy();
  }
}
